import { create } from "zustand";
import type { Friend, UpdateFriendRequest } from "@boo/shared";
import {
  createFriend,
  deleteFriend,
  fetchFriends,
  mergeFriend,
  removeFriendAvatar,
  setFriendEmoji,
  updateFriend,
  uploadFriendAvatar,
} from "@/services/friendApi";

const byName = (a: Friend, b: Friend) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const replace = (
  set: (fn: (s: FriendsState) => Partial<FriendsState>) => void,
  updated: Friend,
) =>
  set((s) => ({
    friends: s.friends.map((f) => (f.id === updated.id ? updated : f)),
  }));

interface FriendsState {
  friends: Friend[];
  loading: boolean;
  loadFriends: () => Promise<void>;
  addFriend: (name: string, contactInfo?: string | null) => Promise<Friend>;
  editFriend: (id: string, patch: UpdateFriendRequest) => Promise<Friend>;
  removeFriend: (id: string) => Promise<void>;
  mergeInto: (id: string, intoId: string) => Promise<void>;
  setEmoji: (id: string, emoji: string) => Promise<void>;
  uploadAvatar: (id: string, photo: Blob) => Promise<void>;
  removeAvatar: (id: string) => Promise<void>;
}

export const useFriendsStore = create<FriendsState>((set) => ({
  friends: [],
  loading: false,

  async loadFriends() {
    set({ loading: true });
    try {
      set({ friends: await fetchFriends() });
    } finally {
      set({ loading: false });
    }
  },

  async addFriend(name, contactInfo) {
    const friend = await createFriend({ name, contactInfo });
    set((s) => ({ friends: [...s.friends, friend].sort(byName) }));
    return friend;
  },

  async editFriend(id, patch) {
    const updated = await updateFriend(id, patch);
    set((s) => ({
      friends: s.friends.map((f) => (f.id === id ? updated : f)).sort(byName),
    }));
    return updated;
  },

  async removeFriend(id) {
    await deleteFriend(id);
    set((s) => ({ friends: s.friends.filter((f) => f.id !== id) }));
  },

  async mergeInto(id, intoId) {
    const merged = await mergeFriend(id, { intoId });
    set((s) => ({
      friends: s.friends
        .filter((f) => f.id !== id)
        .map((f) => (f.id === intoId ? merged : f)),
    }));
  },

  async setEmoji(id, emoji) {
    replace(set, await setFriendEmoji(id, emoji));
  },

  async uploadAvatar(id, photo) {
    replace(set, await uploadFriendAvatar(id, photo));
  },

  async removeAvatar(id) {
    replace(set, await removeFriendAvatar(id));
  },
}));
